// src/screens/PostLeversComparison.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 42 Section 4 — Post-Levers Comparison
// Side-by-side before/after view of what the client's lever decisions changed.
// First screen in Close movement, before WhatTodayChanged.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer, Legend, Tooltip,
} from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency, formatCurrencyK } from '../utils/format';
import { POT_COLOURS } from '../constants/potColours';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
  originalPensionContribution?: number;
  originalIsaContribution?: number;
}

const fmt = formatCurrency;
const fmtK = formatCurrencyK;

const PENSION_GROWTH = 0.08;
const ISA_GROWTH = 0.08;
const PROPERTY_GROWTH = 0.01;
const CASH_GROWTH = 0.01;

// ── Projection helper ──────────────────────────────────────────────────────
function projectNetWorth(
  data: ClientData,
  pensionMonthly: number,
  isaMonthly: number,
  mortgageOP: number,
  savingsMonthly: number,
  years: number,
): number[] {
  let pension = data.pensionBalance || 0;
  let isa = data.investmentsBalance || 0;
  let cash = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0);
  let mortBal = data.mortgageBalance || 0;
  const mortRate = (data.mortgageRate || 0) / 100;
  const mortPayment = data.mortgagePayment || 0;
  const propValue = data.propertyValue || 0;

  const trajectory: number[] = [];

  for (let t = 0; t <= years; t++) {
    const propNet = propValue * Math.pow(1 + PROPERTY_GROWTH, t) - Math.max(0, mortBal);
    const netWorth = pension + isa + cash + propNet;
    trajectory.push(Math.round(netWorth));

    // Grow for next year
    pension = pension * (1 + PENSION_GROWTH) + pensionMonthly * 12;
    isa = isa * (1 + ISA_GROWTH) + isaMonthly * 12;
    cash = cash * (1 + CASH_GROWTH) + savingsMonthly * 12;

    // Mortgage reduction
    if (mortBal > 0) {
      const interest = mortBal * mortRate;
      const principal = Math.max(0, (mortPayment + mortgageOP) * 12 - interest);
      mortBal = Math.max(0, mortBal - principal);
    }
  }

  return trajectory;
}

export default function PostLeversComparison({ data, originalPensionContribution, originalIsaContribution }: Props) {
  const age = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 60;
  const years = Math.max(0, freedomAge - age);
  const target = data.freedomNumberInflated || 0;

  // ── Before state (pre-lever snapshot, set once on load) ─────────────────
  const beforePension = originalPensionContribution ?? (data.pensionCompanyFunded || 0);
  const beforeISA = originalIsaContribution ?? (data.isaContribution || 0);
  const beforeSavings = 0; // no structured savings before plan
  const beforeMortgageOP = 0; // no overpayment before lever

  // ── After state (post-lever plan values) ──────────────────────────────────
  const afterPension = (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const afterISA = data.planIsaMonthlyContribution || data.isaContribution || 0;
  const afterSavings = data.planEfMonthlyTopUp || 0;
  const afterMortgageOP = data.planMortgageOverpayment || data.mortgageOP || 0;

  // ── Deltas ────────────────────────────────────────────────────────────────
  const deltaPension = afterPension - beforePension;
  const deltaISA = afterISA - beforeISA;
  const deltaSavings = afterSavings - beforeSavings;
  const deltaMortgage = afterMortgageOP - beforeMortgageOP;
  const totalMonthlyDelta = Math.max(0, deltaPension + deltaISA + deltaSavings + deltaMortgage);

  // Tax saved from pension lever
  const pensionTaxSaving = data.planTaxSavedPerYear || (data.totalTaxSavedAnnual || 0);

  // Gap reduced
  const annualGapDelta = data.totalGapClosed || 0;

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (years <= 0) return [];
    const beforeTrajectory = projectNetWorth(data, beforePension, beforeISA, beforeMortgageOP, beforeSavings, years);
    const afterTrajectory = projectNetWorth(data, afterPension, afterISA, afterMortgageOP, afterSavings, years);

    return beforeTrajectory.map((_, t) => ({
      age: age + t,
      before: beforeTrajectory[t],
      after: afterTrajectory[t],
    }));
  }, [data, age, years, beforePension, beforeISA, beforeMortgageOP, beforeSavings,
      afterPension, afterISA, afterMortgageOP, afterSavings]);

  // ── Delta cards config ────────────────────────────────────────────────────
  const deltaCards = [
    { label: 'Pension', delta: deltaPension, sub: '/month more invested', icon: '🏛️' },
    { label: 'ISA', delta: deltaISA, sub: '/month more invested', icon: '📈' },
    { label: 'Savings', delta: deltaSavings, sub: '/month more saved', icon: '💰' },
    { label: 'Mortgage', delta: deltaMortgage, sub: '/month overpayment added', icon: '🏠' },
    { label: 'Gap reduced', delta: annualGapDelta, sub: 'towards Freedom Number', icon: '🎯', isAnnual: false },
    { label: 'Tax saved', delta: pensionTaxSaving, sub: 'per year', icon: '🧾', isAnnual: true },
  ].filter(c => c.delta > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
      <div className="max-w-6xl mx-auto">

        {/* ── Headline ─────────────────────────────────────────────────────── */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-black text-ma-navy mb-2">Your Plan — Before &amp; After</h1>
          <p className="text-lg text-gray-500">
            Today, you moved{' '}
            <span className="font-bold" style={{ color: '#64DAD2' }}>{fmt(Math.round(totalMonthlyDelta))}/month</span>
            {' '}from lifestyle to your future.
          </p>
        </div>

        {/* ── Delta Cards ──────────────────────────────────────────────────── */}
        {deltaCards.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
            {deltaCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-xl border border-gray-200 p-4 text-center"
              >
                <div className="text-2xl mb-1">{card.icon}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide font-medium">{card.label}</div>
                <div className="text-lg font-bold" style={{ color: '#64DAD2' }}>
                  {card.isAnnual ? fmt(Math.round(card.delta)) : `${fmt(Math.round(card.delta))}`}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{card.sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Wealth Trajectory Comparison ──────────────────────────────────── */}
        {chartData.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-bold text-ma-navy mb-4">Wealth Trajectory — Before vs After</h3>
            <div style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="age"
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    label={{ value: 'Age', position: 'insideBottom', offset: -10, fill: '#9ca3af', fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={(v: number) => {
                      if (v >= 1000000) return `£${(v / 1000000).toFixed(1)}m`;
                      return fmtK(v);
                    }}
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                  />
                  <Tooltip formatter={(v: any) => fmt(Number(v))} />
                  <Line
                    type="monotone"
                    dataKey="before"
                    stroke="#9CA3AF"
                    strokeWidth={2}
                    strokeDasharray="8 4"
                    dot={false}
                    name="Before today"
                  />
                  <Line
                    type="monotone"
                    dataKey="after"
                    stroke="#64DAD2"
                    strokeWidth={3}
                    dot={false}
                    name="After today"
                  />
                  {target > 0 && (
                    <ReferenceLine
                      y={target}
                      stroke="#EF4444"
                      strokeDasharray="8 4"
                      strokeWidth={2}
                      label={{
                        value: `Freedom Number ${fmtK(target)}`,
                        position: 'insideTopRight',
                        fill: '#EF4444',
                        fontSize: 11,
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                  <Legend
                    verticalAlign="top"
                    wrapperStyle={{ paddingBottom: 10 }}
                    iconType="line"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ── Footnote ─────────────────────────────────────────────────────── */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-xs text-gray-400">
          Projections assume 8% pension/ISA growth, 1% property/cash growth, 2.5% inflation.
          Planning purposes only.
        </div>
      </div>
    </div>
  );
}
